import { ListChecks } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { DeviceModelDefinition } from "../models/device-model-catalog";
import { defaultPalletRackLayerHeights } from "../objects/pallet-rack";
import { maxProjectSceneStateBytes, type ProjectSceneState } from "../projects/project-types";
import {
  type CargoEditorBatchSettingRegion,
  type CargoEditorCameraState,
  type CargoEditorCargoAsset,
  type CargoEditorDeviceBatchSelection,
  type CargoEditorDxfReference,
  type CargoEditorDxfStage,
  type CargoEditorPalletReciprocatingLiftBatchSelection,
  type CargoEditorPalletStackerCraneBatchSelection,
  type CargoEditorRackBatchSelection,
  type CargoEditorRackConfiguration,
  type CargoEditorRackSelection,
  type CargoEditorSceneController,
  type CargoEditorSelection,
  type CargoEditorSingleStationReciprocatingShuttleBatchSelection,
  createCargoEditorScene
} from "../scenes/cargo-editor-scene";
import { type CargoRenderOptions, defaultCargoRenderOptions } from "../scenes/cargo-render-mode";
import {
  applyBatchRackCargo,
  applyBatchRackCargoCapacity,
  applyBatchRackLayerHeights,
  getProjectSceneStateByteLength,
  pruneBatchRackCargoContents,
  replaceBatchRackCargoContents
} from "./cargo-batch-state";
import { CargoRegionSettingsDialog } from "./cargo-region-settings-dialog";
import { type CargoCameraView, CargoSceneBrowser } from "./cargo-scene-browser";
import { CargoSceneSettings } from "./cargo-scene-settings";
import { createProjectRackCargo, replaceRackCargoContents } from "./rack-cargo-bulk-state";
import { SceneLoading, type SceneLoadingState } from "./scene-loading";

type EditorLoadState = SceneLoadingState | "ready";
type CargoContents = Record<string, CargoEditorCargoAsset>;
type DxfReferenceState = {
  activeFileName?: string;
  error?: string;
  loadingFileName?: string;
  metadata?: CargoEditorDxfReference;
  stage?: CargoEditorDxfStage;
};

const initialRackConfiguration: CargoEditorRackConfiguration = {
  cargoCapacity: 1,
  cargoCellLength: 0,
  cargoCellWidth: 0,
  cargoRowCount: 1,
  layerHeights: [...defaultPalletRackLayerHeights],
  lengthCellCount: 0
};

type CargoSceneEditorProps = {
  deviceModels: DeviceModelDefinition[];
  drawingFile: File;
  initialSceneState: ProjectSceneState;
  onManageDevices: () => void;
  onRemoveDrawing: () => Promise<void>;
  onReplaceDrawing: (file: File) => Promise<void>;
  onSceneStateChange: (state: ProjectSceneState) => void;
};

export default function CargoSceneEditor({
  deviceModels,
  drawingFile,
  initialSceneState,
  onManageDevices,
  onRemoveDrawing,
  onReplaceDrawing,
  onSceneStateChange
}: CargoSceneEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<CargoEditorSceneController | undefined>(undefined);
  const dxfRequestRef = useRef(0);
  const initialDeviceModelsRef = useRef(deviceModels);
  const initialSceneStateRef = useRef(initialSceneState);
  const sceneStateRef = useRef(initialSceneState);
  const [busy, setBusy] = useState(false);
  const [batchError, setBatchError] = useState<string | undefined>();
  const [batchSettingRegions, setBatchSettingRegions] = useState<CargoEditorBatchSettingRegion[]>(
    []
  );
  const [multiSelectionActive, setMultiSelectionActive] = useState(false);
  const [activeCameraViewId, setActiveCameraViewId] = useState<string | undefined>();
  const [cameraViews, setCameraViews] = useState<CargoCameraView[]>(initialSceneState.cameraViews);
  const [cargoContents, setCargoContents] = useState<CargoContents>({});
  const [dxfReference, setDxfReference] = useState<DxfReferenceState>({});
  const [exportError, setExportError] = useState<string | undefined>();
  const [exporting, setExporting] = useState(false);
  const [imageError, setImageError] = useState<string | undefined>();
  const [includeCadInImage, setIncludeCadInImage] = useState(true);
  const [isBrowseMode, setIsBrowseMode] = useState(false);
  const [isRegionDialogOpen, setIsRegionDialogOpen] = useState(false);
  const [isPositioningCamera, setIsPositioningCamera] = useState(false);
  const [pendingCameraState, setPendingCameraState] = useState<
    CargoEditorCameraState | undefined
  >();
  const [rackConfiguration, setRackConfiguration] = useState(initialRackConfiguration);
  const [loadState, setLoadState] = useState<EditorLoadState>("loading");
  const [renderingImage, setRenderingImage] = useState(false);
  const [renderOptions, setRenderOptions] = useState<CargoRenderOptions>(
    initialSceneState.renderOptions ?? defaultCargoRenderOptions
  );
  const [selection, setSelection] = useState<CargoEditorSelection | undefined>();

  useEffect(() => {
    const host = hostRef.current;

    if (!host) {
      return;
    }

    let controller: CargoEditorSceneController | undefined;
    let disposed = false;

    setLoadState("loading");
    void createCargoEditorScene(host, {
      deviceModels: initialDeviceModelsRef.current,
      renderOptions: initialSceneStateRef.current.renderOptions ?? defaultCargoRenderOptions,
      onMultiSelectionModeChange: setMultiSelectionActive,
      onSelectionChange(nextSelection) {
        setBatchError(undefined);
        setSelection(nextSelection);
        if (nextSelection?.kind === "rack-cell") {
          setCargoContents((current) => {
            const next = { ...current };
            nextSelection.cargo.forEach((asset, slotIndex) => {
              const key = createCargoKey(nextSelection, slotIndex);
              if (asset) {
                next[key] = asset;
              } else {
                delete next[key];
              }
            });
            return next;
          });
          setRackConfiguration({
            cargoCapacity: nextSelection.cargoCapacity,
            cargoCellLength: nextSelection.cargoCellLength,
            cargoCellWidth: nextSelection.cargoCellWidth,
            cargoRowCount: nextSelection.cargoRowCount,
            layerHeights: [...nextSelection.layerHeights],
            lengthCellCount: nextSelection.lengthCellCount
          });
        }
      }
    })
      .then((sceneController) => {
        if (disposed) {
          sceneController.dispose();
          return;
        }

        controller = sceneController;
        controllerRef.current = sceneController;
        setLoadState("ready");
      })
      .catch((error: unknown) => {
        if (!disposed) {
          console.error("Failed to create cargo scene editor", error);
          setLoadState("error");
        }
      });

    return () => {
      disposed = true;
      dxfRequestRef.current += 1;
      if (controllerRef.current === controller) {
        controllerRef.current = undefined;
      }
      controller?.dispose();
    };
  }, []);

  const updateSceneState = useCallback(
    (update: (current: ProjectSceneState) => ProjectSceneState) => {
      const next = update(sceneStateRef.current);
      sceneStateRef.current = next;
      onSceneStateChange(next);
    },
    [onSceneStateChange]
  );

  const refreshBatchSettingRegions = useCallback(() => {
    setBatchSettingRegions(controllerRef.current?.getBatchSettingRegions() ?? []);
  }, []);

  const selectedCargo =
    selection && selection.kind === "rack-cell"
      ? Array.from({ length: rackConfiguration.cargoCapacity }, (_, slotIndex) => ({
          asset: cargoContents[createCargoKey(selection, slotIndex)],
          slotIndex
        }))
      : [];

  const handleCargoChange = async (slotIndex: number, asset?: CargoEditorCargoAsset) => {
    const controller = controllerRef.current;
    const cell = selection?.kind === "rack-cell" ? selection : undefined;

    if (!controller || !cell) {
      return;
    }

    setBusy(true);
    try {
      await controller.setCargo(cell, slotIndex, asset);
      const key = createCargoKey(cell, slotIndex);
      setCargoContents((current) => {
        const next = { ...current };
        if (asset) {
          next[key] = asset;
        } else {
          delete next[key];
        }
        return next;
      });
      updateSceneState((current) => {
        const rack = current.racks[cell.rackId] ?? { cargo: {}, cargoCapacities: {} };
        return {
          ...current,
          racks: {
            ...current.racks,
            [cell.rackId]: {
              ...rack,
              cargo: {
                ...rack.cargo,
                [createProjectCargoKey(cell, slotIndex)]: asset ?? null
              }
            }
          }
        };
      });
    } catch (error: unknown) {
      console.error("Failed to update cargo", error);
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleRackConfigurationChange = (nextConfiguration: CargoEditorRackConfiguration) => {
    const controller = controllerRef.current;
    const rackSelection = selection?.kind === "rack-cell" ? selection : undefined;

    if (!controller || !rackSelection) {
      return;
    }

    setRackConfiguration(nextConfiguration);
    setCargoContents((current) => pruneCargoContents(current, rackSelection, nextConfiguration));
    void controller
      .setRackConfiguration(rackSelection.rackId, rackSelection.cellId, nextConfiguration)
      .catch((error: unknown) => {
        console.error("Failed to update rack configuration", error);
      });
    updateSceneState((current) => {
      const rack = current.racks[rackSelection.rackId] ?? { cargo: {}, cargoCapacities: {} };
      return {
        ...current,
        racks: {
          ...current.racks,
          [rackSelection.rackId]: {
            ...rack,
            cargo: pruneProjectCargoOverrides(rack.cargo, rackSelection, nextConfiguration),
            cargoCapacities: {
              ...rack.cargoCapacities,
              [rackSelection.cellId]: nextConfiguration.cargoCapacity
            },
            layerHeightMode: "clear",
            layerHeights: [...nextConfiguration.layerHeights]
          }
        }
      };
    });
  };

  const handleRackCargoChange = async (asset?: CargoEditorCargoAsset) => {
    const controller = controllerRef.current;
    const rackSelection = selection?.kind === "rack-cell" ? selection : undefined;
    if (!controller || !rackSelection || busy) {
      return;
    }

    setBusy(true);
    try {
      const updates = await controller.setRackCargo(rackSelection.rackId, asset);
      if (!updates) {
        return;
      }
      setCargoContents((current) =>
        replaceRackCargoContents(current, rackSelection.rackId, updates)
      );
      updateSceneState((current) => {
        const rack = current.racks[rackSelection.rackId] ?? { cargo: {}, cargoCapacities: {} };
        return {
          ...current,
          racks: {
            ...current.racks,
            [rackSelection.rackId]: {
              ...rack,
              cargo: createProjectRackCargo(updates)
            }
          }
        };
      });
    } catch (error: unknown) {
      console.error("Failed to replace rack cargo", error);
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleRenderOptionsChange = async (nextOptions: CargoRenderOptions) => {
    const controller = controllerRef.current;
    if (!controller || busy) {
      return;
    }

    const previousOptions = renderOptions;
    setRenderOptions(nextOptions);
    setBusy(true);
    try {
      await controller.setRenderOptions(nextOptions);
      updateSceneState((current) => ({ ...current, renderOptions: nextOptions }));
    } catch (error: unknown) {
      console.error("Failed to update render options", error);
      setRenderOptions(previousOptions);
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleDeviceModelChange = async (deviceId: string, modelId: string) => {
    const controller = controllerRef.current;
    if (!controller || busy) {
      return;
    }

    setBusy(true);
    try {
      await controller.setDeviceModel(deviceId, modelId);
      updateSceneState((current) => ({
        ...current,
        devices: { ...current.devices, [deviceId]: modelId }
      }));
    } catch (error: unknown) {
      console.error("Failed to update device model", error);
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handlePalletReciprocatingLiftLayerCountChange = (liftId: string, layerCount: number) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }

    controller.setPalletReciprocatingLiftLayerCount(liftId, layerCount);
    updateSceneState((current) => ({
      ...current,
      lifts: { ...current.lifts, [liftId]: layerCount }
    }));
    setSelection((current) =>
      current?.kind === "pallet-reciprocating-lift" && current.liftId === liftId
        ? { ...current, layerCount }
        : current
    );
  };

  const handleSingleStationReciprocatingShuttleHeightChange = (
    shuttleId: string,
    height: number
  ) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }

    controller.setSingleStationReciprocatingShuttleHeight(shuttleId, height);
    updateSceneState((current) => ({
      ...current,
      singleStationReciprocatingShuttles: {
        ...current.singleStationReciprocatingShuttles,
        [shuttleId]: height
      }
    }));
    setSelection((current) =>
      current?.kind === "single-station-reciprocating-shuttle" && current.shuttleId === shuttleId
        ? { ...current, height }
        : current
    );
  };
  const handlePalletStackerCraneHeightChange = (stackerCraneId: string, height: number) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }

    controller.setPalletStackerCraneHeight(stackerCraneId, height);
    updateSceneState((current) => ({
      ...current,
      palletStackerCranes: {
        ...current.palletStackerCranes,
        [stackerCraneId]: height
      }
    }));
    setSelection((current) =>
      current?.kind === "pallet-stacker-crane" && current.stackerCraneId === stackerCraneId
        ? { ...current, height }
        : current
    );
  };

  const handleRackBatchCargoChange = async (
    batch: CargoEditorRackBatchSelection,
    asset?: CargoEditorCargoAsset
  ) => {
    const controller = controllerRef.current;
    if (!controller || busy) {
      return;
    }
    const plannedUpdates = controller.getRackCargoUpdatesForSelection(batch.rackIds, asset);
    if (!plannedUpdates) {
      return;
    }
    const nextState = applyBatchRackCargo(sceneStateRef.current, plannedUpdates);
    if (getProjectSceneStateByteLength(nextState) > maxProjectSceneStateBytes) {
      setBatchError("批量货物配置会使项目设置超过 1 MB，已取消操作。");
      return;
    }

    setBusy(true);
    setBatchError(undefined);
    try {
      for (const rackId of batch.rackIds) {
        if (controllerRef.current !== controller) {
          return;
        }
        const updates = await controller.setRackCargoForSelection([rackId], asset);
        if (!updates) {
          return;
        }
        setCargoContents((current) => replaceBatchRackCargoContents(current, updates));
        updateSceneState((current) => applyBatchRackCargo(current, updates));
      }
    } catch (error: unknown) {
      console.error("Failed to replace batch rack cargo", error);
      setBatchError(error instanceof Error ? error.message : "批量设置货物失败");
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleRackBatchCargoCapacityChange = async (
    batch: CargoEditorRackBatchSelection,
    cargoCapacity: number
  ) => {
    const controller = controllerRef.current;
    if (!controller || busy) {
      return;
    }
    setBusy(true);
    setBatchError(undefined);
    try {
      const cellIdsByRack = await controller.setRackCargoCapacityForSelection(
        batch.rackIds,
        cargoCapacity
      );
      if (!cellIdsByRack) {
        return;
      }
      setCargoContents((current) =>
        pruneBatchRackCargoContents(current, batch.rackIds, { cargoCapacity })
      );
      updateSceneState((current) =>
        applyBatchRackCargoCapacity(current, cellIdsByRack, cargoCapacity)
      );
    } catch (error: unknown) {
      console.error("Failed to update batch rack cargo capacity", error);
      setBatchError(error instanceof Error ? error.message : "批量设置货位数量失败");
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleRackBatchLayerHeightsChange = async (
    batch: CargoEditorRackBatchSelection,
    layerHeights: number[]
  ) => {
    const controller = controllerRef.current;
    if (!controller || busy) {
      return;
    }
    setBusy(true);
    setBatchError(undefined);
    try {
      await controller.setRackLayerHeightsForSelection(batch.rackIds, layerHeights);
      setCargoContents((current) =>
        pruneBatchRackCargoContents(current, batch.rackIds, {
          layerCount: layerHeights.length
        })
      );
      updateSceneState((current) =>
        applyBatchRackLayerHeights(current, batch.rackIds, layerHeights)
      );
    } catch (error: unknown) {
      console.error("Failed to update batch rack layer heights", error);
      setBatchError(error instanceof Error ? error.message : "批量设置货架层高失败");
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleDeviceBatchModelChange = async (
    batch: CargoEditorDeviceBatchSelection,
    modelId: string
  ) => {
    const controller = controllerRef.current;
    if (!controller || busy) {
      return;
    }
    setBusy(true);
    setBatchError(undefined);
    try {
      await controller.setDeviceModelsForSelection(batch.deviceIds, modelId);
      updateSceneState((current) => ({
        ...current,
        devices: {
          ...current.devices,
          ...Object.fromEntries(batch.deviceIds.map((deviceId) => [deviceId, modelId]))
        }
      }));
    } catch (error: unknown) {
      console.error("Failed to update batch device models", error);
      setBatchError(error instanceof Error ? error.message : "批量切换设备型号失败");
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
      }
    }
  };

  const handleLiftBatchLayerCountChange = (
    batch: CargoEditorPalletReciprocatingLiftBatchSelection,
    layerCount: number
  ) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }
    setBatchError(undefined);
    controller.setPalletReciprocatingLiftLayerCountForSelection(batch.liftIds, layerCount);
    updateSceneState((current) => ({
      ...current,
      lifts: {
        ...current.lifts,
        ...Object.fromEntries(batch.liftIds.map((liftId) => [liftId, layerCount]))
      }
    }));
  };

  const handleShuttleBatchHeightChange = (
    batch: CargoEditorSingleStationReciprocatingShuttleBatchSelection,
    height: number
  ) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }
    setBatchError(undefined);
    controller.setSingleStationReciprocatingShuttleHeightForSelection(batch.shuttleIds, height);
    updateSceneState((current) => ({
      ...current,
      singleStationReciprocatingShuttles: {
        ...current.singleStationReciprocatingShuttles,
        ...Object.fromEntries(batch.shuttleIds.map((shuttleId) => [shuttleId, height]))
      }
    }));
  };
  const handleStackerCraneBatchHeightChange = (
    batch: CargoEditorPalletStackerCraneBatchSelection,
    height: number
  ) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }
    setBatchError(undefined);
    controller.setPalletStackerCraneHeightForSelection(batch.stackerCraneIds, height);
    updateSceneState((current) => ({
      ...current,
      palletStackerCranes: {
        ...current.palletStackerCranes,
        ...Object.fromEntries(
          batch.stackerCraneIds.map((stackerCraneId) => [stackerCraneId, height])
        )
      }
    }));
  };

  useEffect(() => {
    const controller = controllerRef.current;
    if (!controller || loadState !== "ready") {
      return;
    }
    void controller
      .setDeviceModels(deviceModels)
      .then(refreshBatchSettingRegions)
      .catch((error: unknown) => {
        console.error("Failed to synchronize device models", error);
      });
  }, [deviceModels, loadState, refreshBatchSettingRegions]);

  const handleDxfFileChange = useCallback(async (file: File) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }

    const requestId = dxfRequestRef.current + 1;
    dxfRequestRef.current = requestId;
    setExportError(undefined);
    setImageError(undefined);
    setDxfReference((current) => ({
      ...current,
      error: undefined,
      loadingFileName: file.name,
      stage: "reading"
    }));

    try {
      const metadata = await controller.loadDxfReference(
        file,
        (stage) => {
          if (dxfRequestRef.current === requestId) {
            setDxfReference((current) => ({ ...current, stage }));
          }
        },
        sceneStateRef.current
      );
      if (dxfRequestRef.current === requestId) {
        setCargoContents({});
        setDxfReference({ activeFileName: file.name, metadata });
        setBatchSettingRegions(controller.getBatchSettingRegions());
      }
    } catch (error: unknown) {
      if (dxfRequestRef.current !== requestId || isAbortError(error)) {
        return;
      }

      console.error("Failed to load DXF floor reference", error);
      setDxfReference((current) => ({
        activeFileName: current.activeFileName,
        error: error instanceof Error ? error.message : "DXF 图纸加载失败",
        metadata: current.metadata
      }));
    }
  }, []);

  const handleExport = async () => {
    const controller = controllerRef.current;
    if (!controller || busy || exporting) {
      return;
    }

    setBusy(true);
    setExporting(true);
    setExportError(undefined);
    try {
      const data = await controller.exportSceneGlb();
      downloadGlb(data, createGlbFileName(dxfReference.activeFileName));
    } catch (error: unknown) {
      console.error("Failed to export cargo scene", error);
      setExportError(error instanceof Error ? error.message : "场景导出失败");
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
        setExporting(false);
      }
    }
  };

  const handleRenderImage = async () => {
    const controller = controllerRef.current;
    if (!controller || busy || renderingImage) {
      return;
    }

    setBusy(true);
    setRenderingImage(true);
    setImageError(undefined);
    try {
      const image = await controller.renderSceneImage({ includeCad: includeCadInImage });
      downloadBlob(image, createPngFileName(dxfReference.activeFileName));
    } catch (error: unknown) {
      console.error("Failed to render cargo scene image", error);
      setImageError(error instanceof Error ? error.message : "场景图片生成失败");
    } finally {
      if (controllerRef.current === controller) {
        setBusy(false);
        setRenderingImage(false);
      }
    }
  };

  const handleEnterBrowseMode = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller || loadState !== "ready" || busy) {
      return;
    }
    controller.setBrowseMode(true);
    setIsBrowseMode(true);
    setIsPositioningCamera(false);
    setPendingCameraState(undefined);
  }, [busy, loadState]);

  const handleExitBrowseMode = useCallback(() => {
    controllerRef.current?.setBrowseMode(false);
    setIsBrowseMode(false);
    setIsPositioningCamera(false);
    setPendingCameraState(undefined);
  }, []);

  const handleStartCameraPositioning = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller || !isBrowseMode) {
      return;
    }
    controller.setBrowseCameraEditing(true);
    setActiveCameraViewId(undefined);
    setIsPositioningCamera(true);
    setPendingCameraState(undefined);
  }, [isBrowseMode]);

  const handleFinishCameraPositioning = useCallback(() => {
    const controller = controllerRef.current;
    if (!controller || !isBrowseMode || !isPositioningCamera) {
      return;
    }
    controller.setBrowseCameraEditing(false);
    setPendingCameraState(controller.captureCameraState());
    setIsPositioningCamera(false);
  }, [isBrowseMode, isPositioningCamera]);

  const handleCancelCameraNaming = useCallback(() => {
    setPendingCameraState(undefined);
  }, []);

  const handleSaveCameraView = useCallback(
    (name: string) => {
      if (!pendingCameraState) {
        return;
      }
      const view: CargoCameraView = {
        id: crypto.randomUUID(),
        name,
        state: pendingCameraState
      };
      setCameraViews((current) => [...current, view]);
      updateSceneState((current) => ({
        ...current,
        cameraViews: [...current.cameraViews, view]
      }));
      setActiveCameraViewId(view.id);
      setPendingCameraState(undefined);
    },
    [pendingCameraState, updateSceneState]
  );

  const handleSelectCameraView = useCallback((view: CargoCameraView) => {
    const controller = controllerRef.current;
    if (!controller) {
      return;
    }
    controller.setBrowseCameraEditing(false);
    controller.setCameraState(view.state);
    setIsPositioningCamera(false);
    setPendingCameraState(undefined);
    setActiveCameraViewId(view.id);
  }, []);

  const handleToggleMultiSelection = () => {
    controllerRef.current?.setMultiSelectionMode(!multiSelectionActive);
  };

  useEffect(() => {
    if (loadState !== "ready") {
      return;
    }

    void handleDxfFileChange(drawingFile).catch((error: unknown) => {
      console.error("Failed to load project DXF file", error);
      setDxfReference({
        error: error instanceof Error ? error.message : `无法读取${drawingFile.name}`
      });
    });
  }, [drawingFile, handleDxfFileChange, loadState]);

  return (
    <main className="cargo-editor-shell">
      <div className={`cargo-editor-workspace${isBrowseMode ? " is-browsing" : ""}`}>
        <section className="cargo-editor-viewport" aria-label="货架三维场景">
          <div className="scene-host" ref={hostRef} />
          {!isBrowseMode ? (
            <button
              aria-label="多选模式"
              aria-pressed={multiSelectionActive}
              className={`cargo-multi-selection-toggle${multiSelectionActive ? " is-active" : ""}`}
              disabled={loadState !== "ready" || busy}
              onClick={handleToggleMultiSelection}
              title="切换多选模式 (B)"
              type="button"
            >
              <ListChecks aria-hidden="true" size={18} />
            </button>
          ) : null}
          {multiSelectionActive && !isBrowseMode ? (
            <div className="cargo-multi-selection-status" role="status">
              <ListChecks aria-hidden="true" size={15} />
              <span>当前为多选模式</span>
            </div>
          ) : null}
          {loadState !== "ready" ? <SceneLoading state={loadState} /> : null}
          {isBrowseMode ? (
            <CargoSceneBrowser
              activeViewId={activeCameraViewId}
              isNaming={Boolean(pendingCameraState)}
              isPositioning={isPositioningCamera}
              onCancelNaming={handleCancelCameraNaming}
              onExit={handleExitBrowseMode}
              onFinishPositioning={handleFinishCameraPositioning}
              onSaveView={handleSaveCameraView}
              onSelectView={handleSelectCameraView}
              onStartPositioning={handleStartCameraPositioning}
              views={cameraViews}
            />
          ) : null}
        </section>
        {!isBrowseMode ? (
          <CargoSceneSettings
            batchError={batchError}
            batchSettingRegionCount={batchSettingRegions.length}
            cargo={selectedCargo}
            configuration={rackConfiguration}
            dxfReference={dxfReference}
            disabled={loadState !== "ready" || busy || Boolean(dxfReference.loadingFileName)}
            exportError={exportError}
            exporting={exporting}
            imageError={imageError}
            includeCadInImage={includeCadInImage}
            onBrowseMode={handleEnterBrowseMode}
            onCargoChange={handleCargoChange}
            onDxfClear={onRemoveDrawing}
            onDxfFileChange={onReplaceDrawing}
            onDeviceModelChange={handleDeviceModelChange}
            onDeviceBatchModelChange={handleDeviceBatchModelChange}
            onExport={handleExport}
            onIncludeCadInImageChange={setIncludeCadInImage}
            onManageDevices={onManageDevices}
            onPalletReciprocatingLiftLayerCountChange={
              handlePalletReciprocatingLiftLayerCountChange
            }
            onPalletReciprocatingLiftBatchLayerCountChange={handleLiftBatchLayerCountChange}
            onRackBatchCargoCapacityChange={handleRackBatchCargoCapacityChange}
            onRackBatchCargoChange={handleRackBatchCargoChange}
            onRackBatchLayerHeightsChange={handleRackBatchLayerHeightsChange}
            onRackCargoChange={handleRackCargoChange}
            onRackConfigurationChange={handleRackConfigurationChange}
            onRenderOptionsChange={handleRenderOptionsChange}
            onRenderImage={handleRenderImage}
            onShowBatchSettingRegions={() => {
              setBatchError(undefined);
              refreshBatchSettingRegions();
              setIsRegionDialogOpen(true);
            }}
            onSingleStationReciprocatingShuttleBatchHeightChange={handleShuttleBatchHeightChange}
            onPalletStackerCraneBatchHeightChange={handleStackerCraneBatchHeightChange}
            onSingleStationReciprocatingShuttleHeightChange={
              handleSingleStationReciprocatingShuttleHeightChange
            }
            onPalletStackerCraneHeightChange={handlePalletStackerCraneHeightChange}
            renderOptions={renderOptions}
            renderingImage={renderingImage}
            selection={selection}
          />
        ) : null}
      </div>
      {isRegionDialogOpen && !isBrowseMode ? (
        <CargoRegionSettingsDialog
          busy={busy}
          error={batchError}
          onClose={() => {
            refreshBatchSettingRegions();
            setIsRegionDialogOpen(false);
          }}
          onDeviceModelChange={handleDeviceBatchModelChange}
          onLiftLayerCountChange={handleLiftBatchLayerCountChange}
          onRackCargoChange={handleRackBatchCargoChange}
          onRackLayerHeightsChange={handleRackBatchLayerHeightsChange}
          onShuttleHeightChange={handleShuttleBatchHeightChange}
          onStackerCraneHeightChange={handleStackerCraneBatchHeightChange}
          regions={batchSettingRegions}
        />
      ) : null}
    </main>
  );
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

function createGlbFileName(dxfFileName?: string) {
  const baseName = dxfFileName?.replace(/\.dxf(?:\.bin)?$/i, "") || "货架场景";
  return `${baseName}-blender-safe.glb`;
}

function createPngFileName(dxfFileName?: string) {
  const baseName = dxfFileName?.replace(/\.dxf(?:\.bin)?$/i, "") || "货架场景";
  return `${baseName}-场景渲染.png`;
}

function downloadGlb(data: ArrayBuffer, fileName: string) {
  downloadBlob(new Blob([data], { type: "model/gltf-binary" }), fileName);
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.download = fileName;
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1_000);
}

function createCargoKey(cell: CargoEditorRackSelection, slotIndex: number) {
  return [cell.rackId, cell.cellId, cell.heightIndex, slotIndex].join("\u0000");
}

function createProjectCargoKey(cell: CargoEditorRackSelection, slotIndex: number) {
  return [cell.cellId, cell.heightIndex, slotIndex].join("\u0000");
}

function pruneProjectCargoOverrides(
  cargo: ProjectSceneState["racks"][string]["cargo"],
  selection: CargoEditorRackSelection,
  configuration: CargoEditorRackConfiguration
) {
  return Object.fromEntries(
    Object.entries(cargo).filter(([key]) => {
      const [cellId, heightValue, slotValue] = key.split("\u0000");
      const heightIndex = Number(heightValue);
      const slotIndex = Number(slotValue);
      return (
        heightIndex < configuration.layerHeights.length &&
        (cellId !== selection.cellId || slotIndex < configuration.cargoCapacity)
      );
    })
  );
}

function pruneCargoContents(
  contents: CargoContents,
  selection: CargoEditorRackSelection,
  configuration: CargoEditorRackConfiguration
) {
  return Object.fromEntries(
    Object.entries(contents).filter(([key]) => {
      const [cargoRackId, cargoCellId, heightValue, slotValue] = key.split("\u0000");
      if (cargoRackId !== selection.rackId) {
        return true;
      }

      const heightIndex = Number(heightValue);
      const slotIndex = Number(slotValue);
      return (
        heightIndex < configuration.layerHeights.length &&
        (cargoCellId !== selection.cellId || slotIndex < configuration.cargoCapacity)
      );
    })
  );
}
